import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from '../../utils/unifiedAuth';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Plus, Edit2, CheckCircle, XCircle, RefreshCw,
  Layers, Tag, Save, X, ChevronRight, Archive, Trash2, RotateCcw
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { getLocalizedField } from '../../context/LanguageContext';
import api from '../../utils/api';


// ── Category Form Modal ─────────────────────────────────────────────
const CategoryFormModal = ({ category, onSave, onClose }) => {
  const [form, setForm] = useState({
    slug: category?.slug || '',
    name: getLocalizedField(category?.name, 'en') || '',
    description: getLocalizedField(category?.description, 'en') || '',
    sort_order: category?.sort_order ?? 99,
  });
  const isEdit = !!category;

  const handleSubmit = () => {
    if (!form.slug.trim() || !form.name.trim()) {
      toast({ title: 'Validation', description: 'Slug and Name are required', variant: 'destructive' });
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold" data-testid="category-modal-title">
              {isEdit ? 'Edit Category' : 'Create Category'}
            </h3>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label>Slug *</Label>
            <Input
              data-testid="category-slug-input"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_') })}
              placeholder="e.g. aesthetics"
              disabled={isEdit}
              className={isEdit ? 'bg-slate-50' : ''}
            />
            {isEdit && <p className="text-xs text-slate-400 mt-1">Slug cannot be changed after creation</p>}
          </div>
          <div>
            <Label>Name *</Label>
            <Input
              data-testid="category-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Aesthetics"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input
              data-testid="category-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
            />
          </div>
          <div>
            <Label>Sort Order</Label>
            <Input
              data-testid="category-sort-order-input"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        <div className="p-5 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="category-save-btn">
            <Save className="h-4 w-4 mr-2" />{isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Treatment Form Modal ────────────────────────────────────────────
// ── Treatment Form Modal ────────────────────────────────────────────
// P0A-FE: Canonically bound to /api/admin/treatments (POST/PUT). The
// legacy ``aliases`` field — part of the dead normalization-shadow
// universe — has been physically removed from the form. Slug + Name +
// Category are the only canonical required inputs.
const TreatmentFormModal = ({ treatment, categorySlug, categories, onSave, onClose }) => {
  const [form, setForm] = useState({
    slug: treatment?.slug || '',
    category_slug: treatment?.category_slug || categorySlug || '',
    display_name: treatment?.display_name || '',
    sort_order: treatment?.sort_order ?? 99,
  });
  const isEdit = !!treatment;

  const handleSubmit = () => {
    if (!form.slug.trim() || !form.display_name.trim() || !form.category_slug) {
      toast({ title: 'Validation', description: 'Slug, Name, and Category are required', variant: 'destructive' });
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="p-5 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold" data-testid="treatment-modal-title">
              {isEdit ? 'Edit Treatment' : 'Create Treatment'}
            </h3>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label>Slug *</Label>
            <Input
              data-testid="treatment-slug-input"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/_/g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') })}
              placeholder="e.g. hair-transplant"
              disabled={isEdit}
              className={isEdit ? 'bg-slate-50' : ''}
            />
          </div>
          <div>
            <Label>Category *</Label>
            <select
              data-testid="treatment-category-select"
              className="w-full border rounded-lg p-2 text-sm"
              value={form.category_slug}
              onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
            >
              <option value="">Select category...</option>
              {categories.map(c => (
                <option key={c.slug} value={c.slug}>{getLocalizedField(c.name, 'en')} ({c.slug})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Name *</Label>
            <Input
              data-testid="treatment-display-name-input"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="e.g. Hair Transplant"
            />
          </div>
          <div>
            <Label>Sort Order</Label>
            <Input
              data-testid="treatment-sort-order-input"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        <div className="p-5 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="treatment-save-btn">
            <Save className="h-4 w-4 mr-2" />{isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────
export default function AdminTreatmentTaxonomy() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catModal, setCatModal] = useState({ open: false, category: null });
  const [treatModal, setTreatModal] = useState({ open: false, treatment: null });
  const [showArchived, setShowArchived] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    try {
      const qs = showArchived ? '?include_archived=true' : '';
      const res = await api.get(`/admin/treatment-taxonomy/categories${qs}`);
      const data = res.data;
      if (data.success) setCategories(data.categories);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load categories', variant: 'destructive' });
    }
  }, [showArchived]);

  const fetchTreatments = useCallback(async (catSlug) => {
    try {
      const params = new URLSearchParams();
      if (catSlug) params.set('category', catSlug);
      if (showArchived) params.set('include_archived', 'true');
      const qs = params.toString();
      const url = `/admin/treatment-taxonomy/treatments${qs ? `?${qs}` : ''}`;
      const res = await api.get(url);
      const data = res.data;
      if (data.success) setTreatments(data.treatments);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load treatments', variant: 'destructive' });
    }
  }, [showArchived]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchCategories();
      await fetchTreatments(selectedCategory);
      setLoading(false);
    })();
  }, [fetchCategories, fetchTreatments, selectedCategory]);

  const selectCategory = (slug) => {
    const next = slug === selectedCategory ? null : slug;
    setSelectedCategory(next);
    fetchTreatments(next);
  };

  // ── Category Actions ──────────────────────────────────────────────
  const handleCategorySave = async (form) => {
    try {
      const res = await api.post(`/admin/treatment-taxonomy/categories/upsert`, form);
      const data = res.data;
      if (data.success) {
        toast({ title: 'Saved', description: `Category "${form.name}" saved` });
        setCatModal({ open: false, category: null });
        await fetchCategories();
      } else {
        toast({ title: 'Error', description: data.detail || 'Save failed', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    }
  };

  const toggleCategoryStatus = async (cat) => {
    // P4-4D: backend now implements soft-delete state machine.
    // For non-deleted rows this button advances: active → inactive → deleted.
    if (cat.status === 'deleted') return;
    try {
      const res = await api.post(`/admin/treatment-taxonomy/categories/disable`, { slug: cat.slug });
      const data = res.data;
      if (data.success) {
        const label = cat.status === 'active' ? 'Deactivated' : 'Deleted';
        toast({ title: label, description: `Category "${getLocalizedField(cat.name, 'en')}" ${label.toLowerCase()}` });
        await fetchCategories();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update category', variant: 'destructive' });
    }
  };

  // P4-4D: Reactivate (inactive → active) / Restore (deleted → active)
  const restoreCategory = async (cat) => {
    await handleCategorySave({
      slug: cat.slug,
      name: cat.name,
      description: cat.description || '',
      sort_order: cat.sort_order,
    });
  };

  // ── Treatment Actions ─────────────────────────────────────────────
  // P0A-FE: Canonical write-door. The legacy
  // ``/admin/treatment-taxonomy/treatments/upsert`` and ``/disable``
  // endpoints were never implemented on the backend; this surface
  // now consumes the canonical ``/api/admin/treatments`` CRUD owned
  // by ``TreatmentContentService.create/update/delete``.
  const handleTreatmentSave = async (form) => {
    try {
      // Map taxonomy admin form fields → canonical write payload.
      const payload = {
        slug: form.slug,
        name: form.display_name,
        category: form.category_slug,
        sortOrder: form.sort_order,
        status: 'published',
        isActive: true,
      };
      const isEdit = treatModal.treatment && treatModal.treatment.slug === form.slug;
      const res = isEdit
        ? await api.put(`/admin/treatments/${encodeURIComponent(form.slug)}`, payload)
        : await api.post(`/admin/treatments`, payload);
      const data = res.data;
      if (data && data.success === true) {
        toast({ title: 'Saved', description: `Treatment "${form.display_name}" saved` });
        setTreatModal({ open: false, treatment: null });
        await fetchTreatments(selectedCategory);
      } else {
        const detail = (data && (data.error || data.detail)) || 'Save failed';
        toast({ title: 'Save failed', description: String(detail), variant: 'destructive' });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Network error';
      toast({ title: 'Save failed', description: String(detail), variant: 'destructive' });
    }
  };

  const toggleTreatmentStatus = async (t) => {
    // P0A-FE: Canonical 2-state model (active / inactive). The legacy
    // 3-state machine (active → inactive → deleted) belonged to the
    // dead taxonomy upsert/disable universe and has been removed.
    // Active rows DELETE → inactive (isActive=false). Inactive rows
    // are restored via ``restoreTreatment``.
    if (t.status === 'inactive') return;
    try {
      const res = await api.delete(`/admin/treatments/${encodeURIComponent(t.slug)}`);
      const data = res.data;
      if (data && data.success === true) {
        toast({ title: 'Deactivated', description: `Treatment "${t.display_name}" deactivated` });
        await fetchTreatments(selectedCategory);
      } else {
        const detail = (data && (data.error || data.detail)) || 'Update failed';
        toast({ title: 'Failed', description: String(detail), variant: 'destructive' });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Network error';
      toast({ title: 'Failed', description: String(detail), variant: 'destructive' });
    }
  };

  // P0A-FE: Restore an inactive treatment via the canonical write-door.
  const restoreTreatment = async (t) => {
    try {
      const res = await api.put(
        `/admin/treatments/${encodeURIComponent(t.slug)}`,
        { isActive: true, status: 'published' },
      );
      const data = res.data;
      if (data && data.success === true) {
        toast({ title: 'Restored', description: `Treatment "${t.display_name}" reactivated` });
        await fetchTreatments(selectedCategory);
      } else {
        const detail = (data && (data.error || data.detail)) || 'Restore failed';
        toast({ title: 'Failed', description: String(detail), variant: 'destructive' });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Network error';
      toast({ title: 'Failed', description: String(detail), variant: 'destructive' });
    }
  };

  const handleRefresh = async () => {
    try {
      await api.post(`/admin/treatment-taxonomy/refresh`);
      toast({ title: 'Refreshed', description: 'In-memory cache refreshed from DB' });
    } catch {
      toast({ title: 'Error', description: 'Refresh failed', variant: 'destructive' });
    }
  };

  const filteredTreatments = selectedCategory
    ? treatments.filter(t => t.category_slug === selectedCategory)
    : treatments;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-treatment-taxonomy-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-cyan-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/treatx-admin/dashboard')}
                className="text-white hover:bg-white/10"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold">Treatment Taxonomy</h1>
                <p className="text-sm text-teal-200">
                  {categories.length} categories, {treatments.length} treatments
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArchived(v => !v)}
                className={`border-white/30 text-white hover:bg-white/10 ${showArchived ? 'bg-white/15' : ''}`}
                data-testid="toggle-archived-btn"
              >
                <Archive className="h-4 w-4 mr-2" />
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="border-white/30 text-white hover:bg-white/10"
                data-testid="refresh-cache-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Cache
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Two-Panel Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          {/* Left Panel: Categories */}
          <div>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Layers className="h-4 w-4 text-teal-600" />
                    Categories
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCatModal({ open: true, category: null })}
                    data-testid="create-category-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y" data-testid="categories-list">
                  {categories.map(cat => {
                    const s = cat.status;
                    return (
                    <div
                      key={cat.slug}
                      data-testid={`category-row-${cat.slug}`}
                      className={`flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors ${
                        selectedCategory === cat.slug
                          ? 'bg-teal-50 border-l-2 border-teal-600'
                          : 'hover:bg-slate-50 border-l-2 border-transparent'
                      } ${s === 'inactive' ? 'opacity-60' : ''} ${s === 'deleted' ? 'bg-red-50/50' : ''}`}
                      onClick={() => selectCategory(cat.slug)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium truncate ${s === 'deleted' ? 'line-through text-red-700' : ''}`}>{getLocalizedField(cat.name, 'en')}</span>
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              s === 'active'
                                ? 'border-green-300 text-green-700'
                                : s === 'deleted'
                                ? 'border-red-400 text-red-700 bg-red-50'
                                : 'border-slate-300 text-slate-500'
                            }`}
                            data-testid={`category-status-${cat.slug}`}
                          >
                            {s}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 truncate">{cat.slug} &middot; order: {cat.sort_order}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); setCatModal({ open: true, category: cat }); }}
                          data-testid={`edit-category-${cat.slug}`}
                          disabled={s === 'deleted'}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        {s !== 'deleted' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 ${
                              s === 'active'
                                ? 'text-red-500 hover:text-red-700'
                                : 'text-red-600 hover:text-red-800'
                            }`}
                            onClick={(e) => { e.stopPropagation(); toggleCategoryStatus(cat); }}
                            data-testid={`toggle-category-${cat.slug}`}
                            title={s === 'active' ? 'Deactivate' : 'Delete'}
                          >
                            {s === 'active' ? <XCircle className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
                          </Button>
                        )}
                        {s !== 'active' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-green-600 hover:text-green-800"
                            onClick={(e) => { e.stopPropagation(); restoreCategory(cat); }}
                            data-testid={`restore-category-${cat.slug}`}
                            title={s === 'deleted' ? 'Restore' : 'Activate'}
                          >
                            {s === 'deleted' ? <RotateCcw className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                          </Button>
                        )}
                      </div>
                      {selectedCategory === cat.slug && (
                        <ChevronRight className="h-4 w-4 text-teal-600 shrink-0" />
                      )}
                    </div>
                  );})}
                  {categories.length === 0 && (
                    <p className="p-4 text-center text-sm text-slate-400">No categories yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel: Treatments */}
          <div>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Tag className="h-4 w-4 text-teal-600" />
                    Treatments
                    {selectedCategory && (
                      <Badge variant="outline" className="text-xs">{selectedCategory}</Badge>
                    )}
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setTreatModal({ open: true, treatment: null })}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="create-treatment-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />Add Treatment
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="treatments-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Slug</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Name</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Status</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-600">Order</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredTreatments.map(t => {
                        const s = t.status;
                        return (
                        <tr
                          key={t.slug}
                          className={`hover:bg-slate-50 ${s === 'inactive' ? 'opacity-60' : ''}`}
                          data-testid={`treatment-row-${t.slug}`}
                        >
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{t.slug}</td>
                          <td className="px-4 py-2.5 font-medium">{t.display_name}</td>
                          <td className="px-4 py-2.5">
                            <Badge
                              variant="outline"
                              className={
                                s === 'active'
                                  ? 'border-green-300 text-green-700 bg-green-50'
                                  : 'border-slate-300 text-slate-500'
                              }
                              data-testid={`treatment-status-${t.slug}`}
                            >
                              {s}
                            </Badge>
                          </td>
                          <td className="px-4 py-2.5 text-slate-500">{t.sort_order}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setTreatModal({ open: true, treatment: t })}
                                data-testid={`edit-treatment-${t.slug}`}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              {s === 'active' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-700"
                                  onClick={() => toggleTreatmentStatus(t)}
                                  data-testid={`toggle-treatment-${t.slug}`}
                                  title="Deactivate"
                                >
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              )}
                              {s !== 'active' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-green-600 hover:text-green-800"
                                  onClick={() => restoreTreatment(t)}
                                  data-testid={`restore-treatment-${t.slug}`}
                                  title="Reactivate"
                                >
                                  <CheckCircle className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );})}
                      {filteredTreatments.length === 0 && (
                        <tr>
                          <td colSpan={5} className="p-6 text-center text-slate-400">
                            {selectedCategory ? 'No treatments in this category' : 'No treatments found'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modals */}
      {catModal.open && (
        <CategoryFormModal
          category={catModal.category}
          onSave={handleCategorySave}
          onClose={() => setCatModal({ open: false, category: null })}
        />
      )}
      {treatModal.open && (
        <TreatmentFormModal
          treatment={treatModal.treatment}
          categorySlug={selectedCategory}
          categories={categories}
          onSave={handleTreatmentSave}
          onClose={() => setTreatModal({ open: false, treatment: null })}
        />
      )}
    </div>
  );
}
